import { FC, useMemo } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Skeleton,
    Paper
} from "@mui/material";
import { useLanguage } from "../../../features/localisation/useLanguage";
import { useDateTimeUtils } from "../../../features/common_funcs/date_time/useDateTimeUtils";

import {IpAddress} from "../../../features/user/models/model";

interface UserIpTableProps {
    ipAddressList?: IpAddress[];
    isLoading: boolean;
}

const UserIpTable: FC<UserIpTableProps> = ({ ipAddressList, isLoading }) => {
    const { languagePack } = useLanguage();
    const { userIpTable } = languagePack.pack;
    const { convertISOToLocaleDateTimeString } = useDateTimeUtils();

    // Convert timestamps when ipAddressList changes
    const formattedIpAddressList = useMemo(() => {
        if (!ipAddressList) return [];

        return ipAddressList.map(ip => ({
            ...ip,
            formatted_first_seen: convertISOToLocaleDateTimeString(ip.first_seen)
        }));
    }, [ipAddressList, convertISOToLocaleDateTimeString]);

    // Render skeleton rows when loading
    const renderSkeletonRows = () => {
        return Array(3).fill(0).map((_, index) => (
            <TableRow key={`skeleton-${index}`}>
                <TableCell>
                    <Skeleton animation="wave" />
                </TableCell>
                <TableCell>
                    <Skeleton animation="wave" />
                </TableCell>
            </TableRow>
        ));
    };

    // Render empty state message when no data
    const renderEmptyState = () => (
        <TableRow>
            <TableCell colSpan={2} align="center">
                <Typography variant="body2" sx={{ py: 2 }}>
                    {userIpTable.noDataMessage}
                </Typography>
            </TableCell>
        </TableRow>
    );

    // Render IP address rows
    const renderIpRows = () => {
        if (!formattedIpAddressList || formattedIpAddressList.length === 0) {
            return renderEmptyState();
        }

        return formattedIpAddressList.map((ip, index) => (
            <TableRow key={`ip-${index}`}>
                <TableCell>{ip.ip_address}</TableCell>
                <TableCell>{ip.formatted_first_seen}</TableCell>
            </TableRow>
        ));
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {userIpTable.title}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{userIpTable.ipAddressHeader}</TableCell>
                            <TableCell>{userIpTable.firstSeenHeader}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? renderSkeletonRows() : renderIpRows()}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default UserIpTable;
